import { useState } from "react";
import { toast } from "sonner";
import { HelpCircle, Sparkles, CheckCircle2, XCircle, ThumbsDown, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  useExplanationForRecommendation, useRequestExplanation, useDecideExplanation, useDisagreeExplanation,
} from "@/lib/explanationQueries";

/**
 * Master v5 Prompt 26 — Bounded contextual AI explanation layer.
 *
 * One "Why this?" entry point, reused on the client Home/Practice
 * Recommendation card AND the coach's Client 360 Recommendation row
 * (`isCoach` is the only behavioural switch). The redaction that keeps
 * a client from ever seeing AI text before coach approval is enforced
 * entirely server-side (`/explanations/latest` never includes
 * `output` unless `status === "approved"`) — this component simply
 * renders whatever the server is willing to hand back.
 */
export function WhyThisButton({ workspaceId, recId, isCoach = false }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs font-medium text-terracotta hover:text-terracotta/80"
        data-testid={`why-this-btn-${recId}`}
      >
        <HelpCircle size={13} /> Why this?
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`why-this-dialog-${recId}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles size={16} className="text-terracotta" /> Why this?</DialogTitle>
          </DialogHeader>
          <ExplanationBody workspaceId={workspaceId} recId={recId} isCoach={isCoach} />
        </DialogContent>
      </Dialog>
    </>
  );
}

function ExplanationBody({ workspaceId, recId, isCoach }) {
  const { data, isPending } = useExplanationForRecommendation(workspaceId, recId);
  const requestExplanation = useRequestExplanation(workspaceId, recId);
  const decide = useDecideExplanation(workspaceId, recId);
  const disagree = useDisagreeExplanation(workspaceId, recId);

  if (isPending) {
    return <p className="text-sm text-ink-soft py-4" data-testid={`why-this-loading-${recId}`}>Loading…</p>;
  }

  const status = data?.status;

  async function handleRequest() {
    try {
      await requestExplanation.mutateAsync();
    } catch {
      toast.error("Couldn't request an explanation right now");
    }
  }

  async function handleDecision(decision) {
    try {
      await decide.mutateAsync({ expId: data.exp_id, decision });
      toast.success(decision === "approved" ? "Explanation approved for the client" : "Explanation rejected");
    } catch {
      toast.error("Couldn't record that decision");
    }
  }

  async function handleDisagree() {
    try {
      await disagree.mutateAsync({ expId: data.exp_id });
      toast.success("Your coach has been notified");
    } catch {
      toast.error("Couldn't send that");
    }
  }

  // No explanation requested yet.
  if (!status) {
    return (
      <div className="py-2" data-testid={`why-this-empty-${recId}`}>
        <p className="text-sm text-ink-soft mb-4">
          Get a short explanation of why this practice item was picked for {isCoach ? "this client" : "you"}.
          {!isCoach && " A coach reviews it before it's shown to you."}
        </p>
        <Button onClick={handleRequest} disabled={requestExplanation.isPending} data-testid={`why-this-request-btn-${recId}`}>
          {requestExplanation.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Sparkles size={14} className="mr-1.5" />}
          Explain this pick
        </Button>
      </div>
    );
  }

  if (status === "generation_failed") {
    return (
      <div className="py-2" data-testid={`why-this-failed-${recId}`}>
        <p className="text-sm text-ink-soft mb-4">
          {isCoach ? `We couldn't generate an explanation: ${data.fallback_reason || "unknown reason"}` : "We couldn't prepare an explanation right now — ask your coach for more detail."}
        </p>
        <Button onClick={handleRequest} disabled={requestExplanation.isPending} variant="outline" data-testid={`why-this-retry-btn-${recId}`}>
          Try again
        </Button>
      </div>
    );
  }

  if (status === "pending_coach_review") {
    if (!isCoach) {
      return (
        <p className="text-sm text-ink-soft py-4 flex items-center gap-2" data-testid={`why-this-pending-member-${recId}`}>
          <Loader2 size={14} className="animate-spin" /> Your coach is reviewing this explanation before it's shown to you.
        </p>
      );
    }
    return (
      <div className="py-2" data-testid={`why-this-pending-coach-${recId}`}>
        <ExplanationOutputView output={data.output} />
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => handleDecision("rejected")} disabled={decide.isPending} data-testid={`why-this-reject-btn-${recId}`}>
            <XCircle size={14} className="mr-1.5" /> Reject
          </Button>
          <Button onClick={() => handleDecision("approved")} disabled={decide.isPending} data-testid={`why-this-approve-btn-${recId}`}>
            <CheckCircle2 size={14} className="mr-1.5" /> Approve for client
          </Button>
        </DialogFooter>
      </div>
    );
  }

  if (status === "rejected") {
    return (
      <div className="py-2" data-testid={`why-this-rejected-${recId}`}>
        <p className="text-sm text-ink-soft mb-4">
          {isCoach ? "You rejected this explanation. Regenerating will create a new version for review." : "This explanation wasn't approved. Ask your coach if you'd like to know more."}
        </p>
        <Button onClick={handleRequest} disabled={requestExplanation.isPending} variant="outline" data-testid={`why-this-regenerate-btn-${recId}`}>
          {isCoach ? "Regenerate" : "Ask again"}
        </Button>
      </div>
    );
  }

  // approved
  return (
    <div className="py-2" data-testid={`why-this-approved-${recId}`}>
      <ExplanationOutputView output={data.output} />
      {!isCoach && (
        data.disagreement_flagged ? (
          <p className="text-xs text-ink-soft mt-4 flex items-center gap-1.5" data-testid={`why-this-flagged-${recId}`}>
            <ThumbsDown size={12} /> Your coach has been let know this didn't feel right.
          </p>
        ) : (
          <button
            onClick={handleDisagree}
            disabled={disagree.isPending}
            className="text-xs text-ink-soft hover:text-terracotta mt-4 flex items-center gap-1.5 disabled:opacity-50"
            data-testid={`why-this-disagree-btn-${recId}`}
          >
            <ThumbsDown size={12} /> This doesn't feel right
          </button>
        )
      )}
      {isCoach && data.disagreement_flagged && (
        <p className="text-xs text-amber-700 mt-4 flex items-center gap-1.5" data-testid={`why-this-flagged-${recId}`}>
          <ThumbsDown size={12} /> The client flagged this explanation as not feeling right.
        </p>
      )}
    </div>
  );
}

function ExplanationOutputView({ output }) {
  if (!output) return null;
  return (
    <div className="space-y-3 text-sm text-ink" data-testid="why-this-output">
      <p>{output.selected_focus_summary}</p>
      <p>{output.approved_intervention_summary}</p>
      {output.alternative_summary && <p className="text-ink-soft">{output.alternative_summary}</p>}
      <p className="text-xs text-ink-soft italic border-t border-ink/10 pt-2">{output.confidence_statement}</p>
    </div>
  );
}
